import { BaseError } from '@smart-tv/contexts/error-context/error-context';

export interface RetryOptions {
  maxRetries?: number;
  baseDelayMs?: number;
  useExponentialBackoff?: boolean;
}

const DEFAULT_OPTIONS: Required<RetryOptions> = {
  maxRetries: 3,
  baseDelayMs: 200,
  useExponentialBackoff: true
};

/**
 * Executes an async function with retry logic and exponential backoff.
 * @param fn - The async function to execute
 * @param options - Retry configuration options
 * @returns The result of the function if successful
 * @throws The last error if all retries fail
 */
export const retryAsync = async <T>(fn: () => Promise<T>, options?: RetryOptions): Promise<T> => {
  const { maxRetries, baseDelayMs, useExponentialBackoff } = {
    ...DEFAULT_OPTIONS,
    ...options
  };

  let lastError: unknown;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error: any) {
      lastError = error;
      if ((error as BaseError).offline !== true) {
        throw error;
      }

      if (attempt < maxRetries - 1) {
        const delay = useExponentialBackoff ? baseDelayMs * Math.pow(2, attempt) : baseDelayMs;
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError;
};
